import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../Support/with_presenter';
import {CalendarMode, EventCalendarPresenter, ScreenType} from './EventCalendarPresenter';
import {endOfWeek, isSameMonth, startOfWeek} from 'date-fns';
import {CreateScheduledEvent} from './Screens/Create/CreateScheduledEvent';
import {Crew, FullCrew} from '../../../Models/Crew';
import {classNames} from '../../Support/classNames';
import {NetworkRequestStatusView} from '../../Components/Utils/NetworkRequestStatusView';
import {CalendarEvent} from '../../../Models/CalendarEvent';
import {EditScheduledEvent} from './Screens/Update/EditScheduledEvent';
import Modal from '../../Components/Modal';
import {Modes} from './Components/Modes';
import {NetworkRequestStatus} from '../../../Network/NetworkResult';
import {Loader} from '../../Components/Utils/Loader';
import {ViewScheduledEvent} from './Screens/View/ViewScheduledEvent';
import {ErrorPlaceholder} from '../../Components/Utils/ErrorPlaceholder';

interface OwnProps {
    crews: FullCrew[];
    className?: string;
}

const EventCalendarComponent: FunctionComponent<OwnProps & PresenterProps<EventCalendarPresenter>> = observer(
    ({className, crews, presenter}) => {
        const screen = presenter.screen;

        const dateRangeString = () => {
            if (presenter.mode === CalendarMode.Week) {
                // Formatted like "Aug 21 - 27, 2021"
                const firstDayOfWeek = startOfWeek(presenter.referenceDate, {weekStartsOn: 1});
                const firstDayOfWeekFormatted = firstDayOfWeek.toLocaleString('en-us', {
                    month: 'short',
                    day: 'numeric',
                });

                const lastDayOfWeek = endOfWeek(presenter.referenceDate, {weekStartsOn: 1});
                const lastDayOfWeekFormatted = lastDayOfWeek.toLocaleString('en-us', {
                    month: isSameMonth(firstDayOfWeek, lastDayOfWeek) ? undefined : 'short',
                    day: 'numeric',
                });
                return `${firstDayOfWeekFormatted} - ${lastDayOfWeekFormatted}, ${presenter.referenceDate.getFullYear()}`;
            }
            //Full written out
            return presenter.referenceDate.toLocaleString('en-us', {month: 'long', year: 'numeric'});
        };

        const me = presenter.me;
        if (me.status === NetworkRequestStatus.Loading) {
            return <Loader />;
        }
        if (me.status === NetworkRequestStatus.Error || presenter.workouts.status === NetworkRequestStatus.Error) {
            return <ErrorPlaceholder large />;
        }

        const availableWorkouts =
            presenter.workouts.status === NetworkRequestStatus.Success ? presenter.workouts.data : [];

        return (
            <>
                {screen.type === ScreenType.Create && (
                    <Modal
                        visible
                        wide
                        onHide={() => {
                            presenter.onFinishCreate();
                        }}
                    >
                        <CreateScheduledEvent
                            date={screen.date}
                            crews={crews}
                            onFinish={() => presenter.onFinishCreate()}
                        />
                    </Modal>
                )}
                {screen.type === ScreenType.Edit && (
                    <EditScheduledEvent
                        calendarEvent={screen.calendarEvent}
                        updateDateTo={screen.updateDateTo}
                        crew={crews.find((crew) => crew.id === screen.calendarEvent.crewId) as Crew}
                        onFinish={() => presenter.onFinishCreate()}
                    />
                )}
                {screen.type === ScreenType.View && (
                    <Modal
                        visible
                        wide
                        onHide={() => {
                            presenter.onFinishCreate();
                        }}
                    >
                        <ViewScheduledEvent
                            me={me.data}
                            crew={crews.find((crew) => crew.id === screen.calendarEvent.crewId) as FullCrew}
                            calendarEvent={screen.calendarEvent}
                            onRequestClose={() => presenter.onRequestCloseModal()}
                            onRequestEdit={() => presenter.onRequestEdit(screen.calendarEvent)}
                        />
                    </Modal>
                )}

                <div className={classNames('card', className)}>
                    <div className="card-title">
                        <div className="row">
                            <div className="col-12 col-sm-6 order-1 order-xl-1 col-xl-4">
                                <button
                                    className="btn btn-outline-secondary ms-2 mt-2"
                                    onClick={() => presenter.onCreateClick(null)}
                                >
                                    Create
                                </button>
                                <button
                                    className="btn btn-outline-primary ms-2 mt-2"
                                    onClick={() => presenter.navigateToday()}
                                >
                                    Today
                                </button>
                            </div>
                            <div className="col-12 order-3 order-xl-2 col-xl-4 d-flex justify-content-center">
                                <h5 className="d-flex flex-row">
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={() => presenter.navigateBack()}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    <div className="calendar-daterange">{dateRangeString()}</div>
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={() => presenter.navigateForward()}
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </h5>
                            </div>
                            <div className="col-12 col-sm-6 order-2 order-xl-3 col-xl-4 d-none d-sm-flex justify-content-end">
                                <div className="btn-group m-2" role="group">
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="calendar-mode"
                                        id="by-month"
                                        value={CalendarMode.Month}
                                        autoComplete="off"
                                        checked={presenter.mode === CalendarMode.Month}
                                        onChange={(e) => presenter.onModeChange(e.currentTarget.value as any)}
                                    />
                                    <label className="btn btn-outline-primary" htmlFor="by-month">
                                        By month
                                    </label>
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="calendar-mode"
                                        id="rsvp"
                                        value={CalendarMode.Rsvp}
                                        autoComplete="off"
                                        checked={presenter.mode === CalendarMode.Rsvp}
                                        onChange={(e) => presenter.onModeChange(e.currentTarget.value as any)}
                                    />
                                    <label className="btn btn-outline-primary" htmlFor="rsvp">
                                        RSVP
                                    </label>
                                    <input
                                        type="radio"
                                        className="btn-check"
                                        name="calendar-mode"
                                        id="by-week"
                                        value={CalendarMode.Week}
                                        autoComplete="off"
                                        checked={presenter.mode === CalendarMode.Week}
                                        onChange={(e) => presenter.onModeChange(e.currentTarget.value as any)}
                                    />
                                    <label className="btn btn-outline-primary" htmlFor="by-week">
                                        By week
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <NetworkRequestStatusView<CalendarEvent[]>
                                result={presenter.calendarEvents}
                                loader={() => {
                                    return (
                                        <Modes
                                            me={me.data}
                                            mode={presenter.mode}
                                            referenceDate={presenter.referenceDate}
                                            ownWorkouts={availableWorkouts}
                                            calendarEvents={[]}
                                            crews={crews}
                                            onCalendarEventDateChange={(calendarEvent, date) => {
                                                presenter.onCalendarEventDateChange(calendarEvent, date);
                                            }}
                                            onCalendarEventClick={(calendarEvent) =>
                                                presenter.onCalendarEventClick(calendarEvent)
                                            }
                                            onCreateClick={(date) => presenter.onCreateClick(date)}
                                        />
                                    );
                                }}
                                error={() => {
                                    return (
                                        <Modes
                                            me={me.data}
                                            mode={presenter.mode}
                                            referenceDate={presenter.referenceDate}
                                            ownWorkouts={availableWorkouts}
                                            calendarEvents={[]}
                                            crews={crews}
                                            onCalendarEventDateChange={(calendarEvent, date) => {
                                                presenter.onCalendarEventDateChange(calendarEvent, date);
                                            }}
                                            onCalendarEventClick={(calendarEvent) =>
                                                presenter.onCalendarEventClick(calendarEvent)
                                            }
                                            onCreateClick={(date) => presenter.onCreateClick(date)}
                                        />
                                    );
                                }}
                                component={(calendarEvents) => {
                                    return (
                                        <Modes
                                            me={me.data}
                                            mode={presenter.mode}
                                            referenceDate={presenter.referenceDate}
                                            ownWorkouts={availableWorkouts}
                                            calendarEvents={calendarEvents}
                                            crews={crews}
                                            onCalendarEventDateChange={(calendarEvent, date) => {
                                                presenter.onCalendarEventDateChange(calendarEvent, date);
                                            }}
                                            onCalendarEventClick={(calendarEvent) =>
                                                presenter.onCalendarEventClick(calendarEvent)
                                            }
                                            onCreateClick={(date) => presenter.onCreateClick(date)}
                                        />
                                    );
                                }}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    },
);

export const EventCalendar = withPresenter<EventCalendarPresenter, OwnProps>(
    (props, component) =>
        new EventCalendarPresenter(props.crews, component.network.workouts, component.events, component.network.me),
    EventCalendarComponent,
);
